// src/components/modules/ExcisePaymentModal.tsx
import {
  Box,
  Button,
  ColumnLayout,
  DatePicker,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';
import { FC, useMemo, useState } from 'react';
import { updateItem } from '../api/generic';
import { useAuthContext } from '../contexts/AuthContext';
import { prefixCollection } from '../utils/prefix';

type MovementLike = {
  id: string;
  amount: number;
  size: number;
  batchId: string;
};

interface ExcisePaymentModalProps {
  visible: boolean;
  onClose: () => void;

  /** Releases for the active period (OUT only) */
  items: MovementLike[];

  /** Function that returns EUR excise for a single movement (already using your per-batch rate logic) */
  computeExciseEUR: (m: MovementLike) => number;

  /** Optional prefilled date (yyyy-mm-dd) */
  defaultDate?: string;
}

const cents = (n: number) => Math.round(n * 100);

const ExcisePaymentModal: FC<ExcisePaymentModalProps> = ({
  visible,
  onClose,
  items,
  computeExciseEUR,
  defaultDate = new Date().toISOString().slice(0, 10),
}) => {
  const { tenant, currency } = useAuthContext();

  const [referenceId, setReferenceId] = useState('');
  const [paidDate, setPaidDate] = useState<string>(defaultDate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const totals = useMemo(() => {
    const exciseEUR = items.reduce((s, it) => s + computeExciseEUR(it), 0);
    const bottles = items.reduce((s, it) => s + (it.amount ?? 0), 0);
    const liters = items.reduce((s, it) => s + (it.size ?? 0) * (it.amount ?? 0), 0);
    return { exciseEUR, exciseCents: cents(exciseEUR), bottles, liters };
  }, [items, computeExciseEUR]);

  const disabled = submitting || !referenceId.trim() || !paidDate || items.length === 0;

  const handleSave = async () => {
    if (disabled) return;
    setSubmitting(true);
    setError(undefined);

    try {
      // Tag each AGP document (release) with payment info
      // You can change the field names to your schema.
      const payload = {
        excisePaidDate: new Date(paidDate),
        excisePaymentReferenceId: referenceId.trim(),
        excisePaidAt: Timestamp.now(), // audit field (optional)
      };

      for (const it of items) {
        await updateItem(prefixCollection('agp', tenant), it.id, payload);
      }

      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to register excise payment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      size="large"
      header="Register paid excise"
      onDismiss={onClose}
      footer={
        <SpaceBetween size="s" direction="horizontal" alignItems="center">
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={submitting} disabled={disabled}>
            Save
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p">
          This will mark <b>{items.length}</b> release {items.length === 1 ? 'entry' : 'entries'} in the current period
          as
          <b> paid</b> and store the payment reference on each AGP item.
        </Box>

        {error && <StatusIndicator type="error">{error}</StatusIndicator>}

        <ColumnLayout columns={2} variant="text-grid">
          <Box>
            <Box variant="awsui-key-label">Releases</Box>
            <Box variant="awsui-value-large">{items.length}</Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Bottles</Box>
            <Box variant="awsui-value-large">{totals.bottles}</Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Liters</Box>
            <Box variant="awsui-value-large">{totals.liters.toFixed(2)} L</Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Excise (period total)</Box>
            <Box variant="awsui-value-large">{currency(totals.exciseEUR.toFixed(2))}</Box>
          </Box>
        </ColumnLayout>

        <FormField label="Payment reference ID" description="Reference/transaction identifier from your tax payment">
          <Input
            placeholder="e.g., OB-2025-08-INV-000123"
            value={referenceId}
            onChange={({ detail }) => setReferenceId(detail.value)}
            autoFocus
          />
        </FormField>

        <FormField label="Paid on">
          <DatePicker value={paidDate} onChange={({ detail }) => setPaidDate(detail.value)} />
        </FormField>

        <Box variant="small">
          Tip: this operation only updates AGP documents in the current period filter (e.g., Month/Quarter/Year).
        </Box>
      </SpaceBetween>
    </Modal>
  );
};

export default ExcisePaymentModal;
